// src/pages/admin/ReportListPage.tsx
// ============================================================
// SALT — Admin Report List Page
//
// Displays a filterable, paginated table of content reports.
// Filters: status (chip-based), content type (dropdown).
// Clicking a row navigates to the report detail page.
// Pending reports are surfaced first.
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosClient from '../../api/axiosClient';

import '../../styles/admin-reports.css';

interface ReportRow {
  reportId: number;
  reporterUsername: string;
  contentType: string;
  targetId: number;
  targetName: string;
  reason: string;
  details: string | null;
  contentUrl: string | null;
  status: string;
  reportCount: number;
  createdAt: string;
}

interface PaginationData {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
}

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'actioned', label: 'Actioned' },
  { value: 'dismissed', label: 'Dismissed' },
  { value: 'cleared', label: 'Cleared' },
];

const CONTENT_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'user_avatar', label: 'User Avatar' },
  { value: 'team_logo', label: 'Team Logo' },
  { value: 'user_bio', label: 'User Bio' },
  { value: 'team_description', label: 'Team Description' },
  { value: 'team_name', label: 'Team Name' },
  { value: 'ign', label: 'IGN' },
];

function formatContentType(ct: string): string {
  return ct.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getStatusClass(status: string): string {
  switch (status) {
    case 'pending': return 'sl-report-badge--pending';
    case 'actioned': return 'sl-report-badge--actioned';
    case 'dismissed': return 'sl-report-badge--dismissed';
    case 'cleared': return 'sl-report-badge--cleared';
    default: return '';
  }
}

export default function ReportListPage() {
  const navigate = useNavigate();

  const [statusFilter, setStatusFilter] = useState('');
  const [contentTypeFilter, setContentTypeFilter] = useState('');
  const [page, setPage] = useState(1);

  const [reports, setReports] = useState<ReportRow[]>([]);
  const [pagination, setPagination] = useState<PaginationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params: Record<string, string | number> = { page, limit: 25 };
      if (statusFilter) params.status = statusFilter;
      if (contentTypeFilter) params.contentType = contentTypeFilter;

      const res = await axiosClient.get('/admin/reports', { params });
      setReports(res.data.data.reports);
      setPagination(res.data.data.pagination);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, contentTypeFilter]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setPage(1);
  };

  return (
    <div className="sl-admin-reports">
      <div className="sl-admin-page-header">
        <h1 className="sl-admin-page-title">Reports</h1>
        {pagination && (
          <p className="sl-admin-page-subtitle">
            {pagination.totalCount} total report{pagination.totalCount !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Filters */}
      <div className="sl-report-filters">
        <div className="sl-report-filter-group sl-report-filter-group--status">
          <span className="sl-report-filter-label">Status</span>
          <div className="sl-report-status-chips">
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`sl-report-status-chip ${statusFilter === opt.value ? 'sl-report-status-chip--active' : ''}`}
                onClick={() => handleStatusChange(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="sl-report-filter-group">
          <span className="sl-report-filter-label">Content Type</span>
          <select
            className="sl-report-filter-select"
            value={contentTypeFilter}
            onChange={(e) => { setContentTypeFilter(e.target.value); setPage(1); }}
          >
            {CONTENT_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="sl-admin-error">{error}</div>}

      {/* Table */}
      <div className="sl-report-table-wrapper">
        <table className="sl-report-table">
          <thead>
            <tr>
              <th className="sl-report-th">ID</th>
              <th className="sl-report-th">Target</th>
              <th className="sl-report-th">Content Type</th>
              <th className="sl-report-th">Reason</th>
              <th className="sl-report-th">Reporter</th>
              <th className="sl-report-th">Status</th>
              <th className="sl-report-th">Pending</th>
              <th className="sl-report-th">Date</th>
            </tr>
          </thead>
          <tbody>
            {loading && reports.length === 0 ? (
              <tr>
                <td colSpan={8} className="sl-report-td sl-report-td--loading">
                  Loading reports...
                </td>
              </tr>
            ) : reports.length === 0 ? (
              <tr>
                <td colSpan={8} className="sl-report-td sl-report-td--empty">
                  No reports found
                </td>
              </tr>
            ) : (
              reports.map((report) => (
                <tr
                  key={report.reportId}
                  className="sl-report-row"
                  onClick={() => navigate(`/admin/reports/${report.reportId}`)}
                >
                  <td className="sl-report-td sl-report-td--id">#{report.reportId}</td>
                  <td className="sl-report-td">
                    <span className="sl-report-target-name">{report.targetName}</span>
                  </td>
                  <td className="sl-report-td">
                    <span className="sl-report-content-type">{formatContentType(report.contentType)}</span>
                  </td>
                  <td className="sl-report-td">
                    <span className="sl-report-reason">{report.reason}</span>
                  </td>
                  <td className="sl-report-td sl-report-td--reporter">{report.reporterUsername}</td>
                  <td className="sl-report-td">
                    <span className={`sl-report-badge ${getStatusClass(report.status)}`}>
                      {report.status}
                    </span>
                  </td>
                  <td className="sl-report-td sl-report-td--count">
                    {report.reportCount > 0 && (
                      <span className={`sl-report-count ${report.reportCount >= 3 ? 'sl-report-count--danger' : ''}`}>
                        {report.reportCount}
                      </span>
                    )}
                  </td>
                  <td className="sl-report-td sl-report-td--date">{formatDate(report.createdAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="sl-report-pagination">
          <button
            className="sl-report-pagination-btn"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
          >
            ← Prev
          </button>
          <span className="sl-report-pagination-info">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            className="sl-report-pagination-btn"
            onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
            disabled={page >= pagination.totalPages || loading}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}